/** Pure-function demo rules-engine evaluator. Wired live into a subset of screens
 * (see `demoWired` scenarios in `src/mocks/data/rulesEngineScenarios.ts`) via
 * `ExceptionBanner`. Takes a snapshot of whatever context is available on the current
 * screen (any/all sections optional) and returns the triggered exceptions for that
 * screen to render. No side effects, no API calls - safe to call on every render/keystroke. */

import { rulesEngineScenarios } from '../mocks/data/rulesEngineScenarios';
import type { RuleSeverity } from '../mocks/data/rulesEngineScenarios';

export interface TriggeredException {
  ruleCode: string;
  title: string;
  message: string;
  severity: RuleSeverity;
  blocking: boolean;
}

export interface RuleEvaluationContext {
  supplier?: {
    isNew?: boolean;
    certificationExpiryDate?: string;
    /** ISO date to evaluate "expired" against; defaults to now. */
    asOfDate?: string;
  };
  product?: {
    allergensComplete?: boolean;
  };
  order?: {
    totalValue?: number;
    budgetRemaining?: number;
    lines?: Array<{
      itemNumber: string;
      itemName?: string;
      isOffContract?: boolean;
      quantity: number;
      averageHistoricalQuantity?: number;
    }>;
  };
  delivery?: {
    orderedQty?: number;
    receivedQty?: number;
  };
  waste?: {
    wastePercentage?: number;
    wasteThresholdPercentage?: number;
  };
  margin?: {
    marginPercentage?: number;
    marginThresholdPercentage?: number;
  };
}

const UNUSUAL_VOLUME_MULTIPLIER = 2;

function buildException(ruleCode: string, message: string): TriggeredException {
  const scenario = rulesEngineScenarios.find((item) => item.code === ruleCode);
  return {
    ruleCode,
    title: scenario?.title ?? ruleCode,
    message,
    severity: scenario?.severity ?? 'Info',
    blocking: scenario?.severity === 'Blocking',
  };
}

export function evaluateRules(context: RuleEvaluationContext): TriggeredException[] {
  const exceptions: TriggeredException[] = [];

  if (context.supplier?.isNew) {
    exceptions.push(buildException(
      'NEW_SUPPLIER_FULL_RECEIPT_COUNT',
      'This supplier is newly onboarded - the first delivery will require a full receipt count instead of a spot check.',
    ));
  }

  if (context.supplier?.certificationExpiryDate) {
    const asOf = context.supplier.asOfDate ? new Date(context.supplier.asOfDate) : new Date();
    if (new Date(context.supplier.certificationExpiryDate) < asOf) {
      exceptions.push(buildException(
        'EXPIRED_CERT_BLOCKS_ORDERING',
        `Food safety certification expired on ${context.supplier.certificationExpiryDate} - ordering is blocked until it's renewed.`,
      ));
    }
  }

  if (context.product?.allergensComplete === false) {
    exceptions.push(buildException(
      'MISSING_ALLERGEN_BLOCKS_LAUNCH',
      'Allergen data is incomplete - this product cannot be launched or trialled until it\u2019s finished.',
    ));
  }

  if (context.order?.totalValue !== undefined && context.order?.budgetRemaining !== undefined
    && context.order.totalValue > context.order.budgetRemaining) {
    exceptions.push(buildException(
      'OVER_BUDGET_NEEDS_APPROVAL',
      `Order total (${context.order.totalValue.toFixed(2)}) exceeds remaining budget (${context.order.budgetRemaining.toFixed(2)}) - sent for head office approval.`,
    ));
  }

  context.order?.lines?.forEach((line) => {
    if (line.isOffContract) {
      exceptions.push(buildException(
        'OFF_CONTRACT_ITEM_FLAGGED',
        `${line.itemName ?? line.itemNumber} is off-contract - flagged for review.`,
      ));
    }
    if (line.averageHistoricalQuantity && line.quantity > line.averageHistoricalQuantity * UNUSUAL_VOLUME_MULTIPLIER) {
      exceptions.push(buildException(
        'UNUSUAL_VOLUME_FLAGGED',
        `${line.itemName ?? line.itemNumber} quantity (${line.quantity}) is more than ${UNUSUAL_VOLUME_MULTIPLIER}x the historical average (${line.averageHistoricalQuantity}) - flagged for manager review.`,
      ));
    }
  });

  if (context.delivery?.orderedQty !== undefined && context.delivery?.receivedQty !== undefined
    && context.delivery.receivedQty < context.delivery.orderedQty) {
    const shortfall = context.delivery.orderedQty - context.delivery.receivedQty;
    const receivedPercentage = context.delivery.orderedQty > 0
      ? Math.round((context.delivery.receivedQty / context.delivery.orderedQty) * 100)
      : 0;
    exceptions.push(buildException(
      'SHORT_DELIVERY_CREDIT_NOTE',
      `Delivery short by ${shortfall} unit(s) - a credit note exception has been raised automatically.`,
    ));
    exceptions.push(buildException(
      'PARTIAL_DELIVERY_UPDATES_FORECAST',
      `Only ${receivedPercentage}% of the order was received - expected stock cover and forecast P&L have been updated.`,
    ));
  }

  if (context.waste?.wastePercentage !== undefined && context.waste?.wasteThresholdPercentage !== undefined
    && context.waste.wastePercentage > context.waste.wasteThresholdPercentage) {
    exceptions.push(buildException(
      'HIGH_WASTE_CREATES_ACTION',
      `Waste (${context.waste.wastePercentage}%) is above the site threshold (${context.waste.wasteThresholdPercentage}%) - a follow-up task has been created.`,
    ));
  }

  if (context.margin?.marginPercentage !== undefined && context.margin?.marginThresholdPercentage !== undefined
    && context.margin.marginPercentage < context.margin.marginThresholdPercentage) {
    exceptions.push(buildException(
      'LOW_MARGIN_ESCALATED',
      `Margin (${context.margin.marginPercentage.toFixed(1)}%) is below the target (${context.margin.marginThresholdPercentage}%) - escalated to commercial.`,
    ));
  }

  return exceptions;
}
